import { useMemo } from 'react';
import { useCustomerDietaryRestrictions } from '@hungryroot/dietary-restrictions';
import { usePairingInventoryParams } from '@hungryroot/inventory';
import { useCustomerServingSize } from '@hungryroot/plan';
import {
  usePairings,
  type CustomerPairingsParamsV3,
  type PairingListResultV3,
} from '@hungryroot/rest';
import { useCustomerPairingsV3, useGetOrdersV3 } from '@hungryroot/rest-hooks';
import { useTagsV3 } from '@hungryroot/tags';

/**
 * Dietary-restriction `field_name` values returned by the customer
 * dietary-restrictions endpoint map 1:1 to boolean query-param keys on
 * the v3 customer-pairings endpoint (e.g. `is_vegan`, `is_gluten_free`).
 *
 * The backend does NOT auto-apply the customer's dietary restrictions
 * server-side — callers must pass each active flag explicitly. This is
 * the same pattern `useReadyToEatProducts` uses for the prepared-meals
 * deflection.
 */
type DietaryRestrictionFlag = keyof Pick<
  CustomerPairingsParamsV3,
  | 'is_gluten_free'
  | 'is_dairy_free'
  | 'is_vegan'
  | 'is_vegetarian'
  | 'is_pescatarian'
  | 'is_nut_free'
  | 'is_peanut_free'
  | 'is_egg_free'
  | 'is_soy_free'
  | 'is_shellfish_free'
  | 'is_sesame_free'
  | 'is_garlic_free'
  | 'is_onion_free'
  | 'is_grain_free'
  | 'is_caffeine_free'
>;

const DIETARY_RESTRICTION_FLAGS: ReadonlySet<DietaryRestrictionFlag> = new Set([
  'is_gluten_free',
  'is_dairy_free',
  'is_vegan',
  'is_vegetarian',
  'is_pescatarian',
  'is_nut_free',
  'is_peanut_free',
  'is_egg_free',
  'is_soy_free',
  'is_shellfish_free',
  'is_sesame_free',
  'is_garlic_free',
  'is_onion_free',
  'is_grain_free',
  'is_caffeine_free',
]);

function isDietaryRestrictionFlag(
  fieldName: string
): fieldName is DietaryRestrictionFlag {
  return DIETARY_RESTRICTION_FLAGS.has(fieldName as DietaryRestrictionFlag);
}

/**
 * Maximum number of novel recipes we display in the deflection grid.
 */
export const NOVEL_RECIPES_DISPLAY_COUNT = 8;

/**
 * Minimum novel-recipe count required to render the deflection at all.
 * Matches the story's "Fallback: return null if fewer than ~12 novel recipes."
 */
export const NOVEL_RECIPES_MIN_THRESHOLD = 12;

/**
 * Pull a generous window of past orders so we don't under-count a recipe
 * as "tried" simply because the customer last ordered it a long time ago.
 * The API caps at 1000 and that is plenty for this use case.
 */
const PAST_ORDERS_LIMIT = 1000;
const HISTORICAL_ORDER_DATE_LIMIT = '2021-08-29';

/**
 * Pool of candidate IDs we pull from the customer-pairings endpoint. We
 * hydrate full pairing details for all of them via /v3/pairings/ so we
 * can filter by `recipe_score` before picking the display set. The
 * endpoint's `count` is the total matching set across all pages and is
 * independent of this limit, so it still reports the true "recipes not
 * yet tried" number.
 */
const CANDIDATE_PAIRINGS_LIMIT = 200;

/**
 * Minimum `recipe_score` we consider "high-quality" enough to surface as
 * the first choice in the deflection grid. Below this we'll still fall
 * back to lower-scored pairings rather than show an under-full grid.
 */
const HIGH_QUALITY_RECIPE_SCORE_MIN = 90;

/**
 * Tag group that identifies a pairing's *primary* protein (Chicken,
 * Salmon, Tofu, etc.). Used to diversify the deflection grid so we
 * don't surface twelve chicken dinners in a row when the customer has
 * a broad-enough catalog to show variety.
 *
 * See the "Primary protein tags" block in `packages/tags/src/tags.ts`
 * and the comment referencing WEB-7140.
 *
 * NOTE: the v3 pairings endpoint returns `tags` as an array of tag IDs
 * (numbers), not expanded Tag objects — the TypeScript type is
 * misleading. To resolve `tag_group_id` we have to round-trip the tag
 * IDs through `useTagsV3`.
 */
const PRIMARY_PROTEIN_TAG_GROUP_ID = 75;

const PAIRING_EXPAND = ['tags'] as const;

export interface UseNovelRecipesResult {
  /** Pairings to display in the grid, capped at NOVEL_RECIPES_DISPLAY_COUNT. */
  recipes: PairingListResultV3[];
  /** Count of unique pairings the customer has ordered in their history. */
  triedCount: number;
  /**
   * Count of recipes in the Hungryroot catalog that match the customer's
   * dietary restrictions, plan serving size, and current inventory — i.e.
   * the recipes the customer could actually receive on their next delivery.
   */
  eligibleCatalogCount: number;
  /**
   * Count of eligible recipes the customer has not yet tried. Computed as
   * `max(0, eligibleCatalogCount - triedCount)`.
   */
  novelCount: number;
  /** True while any underlying request is still in flight. */
  isLoading: boolean;
  /** True when any underlying request failed. We treat errors as empty. */
  isError: boolean;
}

/**
 * Returns:
 * - how many unique recipes the customer has ever ordered ("tried"),
 * - how many catalog recipes match their plan (dietary restrictions +
 *   serving size) and are in stock for their next delivery, and
 * - a handful of actual pairing objects to display in the deflection grid.
 *
 * The eligible catalog count is pulled live from the `count` field on the
 * paginated v3 customer-pairings response, so the number reflects the
 * actual catalog the customer could receive — not a static estimate.
 *
 * Used by the NotEnoughUniqueRecipesDeflection to surface catalog breadth.
 */
export function useNovelRecipes(): UseNovelRecipesResult {
  // 1. Past orders → count of unique pairing IDs the customer has ordered.
  const {
    data: ordersData,
    isLoading: isOrdersLoading,
    isError: isOrdersError,
  } = useGetOrdersV3({
    time_bucket: 'past',
    preferred_date_after: HISTORICAL_ORDER_DATE_LIMIT,
    limit: PAST_ORDERS_LIMIT,
    expand: ['bundles'],
  });

  const triedPairingIds = useMemo(() => {
    const ids = new Set<number>();
    for (const order of ordersData?.results ?? []) {
      for (const bundle of order.bundles ?? []) {
        if (bundle.pairing_id) {
          ids.add(bundle.pairing_id);
        }
      }
    }
    return ids;
  }, [ordersData?.results]);

  const triedCount = triedPairingIds.size;

  // 2. Customer-eligible pairings.
  //
  //    Backend applies the customer's dietary restrictions server-side.
  //    We additionally filter by the customer's current plan serving size
  //    (2- vs 4-serving recipes) and by inventory for their next editable
  //    order. The response's `count` reflects the full matching set
  //    (across all pages), which is what we use for the stat.
  //
  //    The backend requires `origin` + an order date whenever
  //    `has_inventory=true` is passed. We derive them from the customer's
  //    next editable order via `usePairingInventoryParams` so inventory
  //    is evaluated against the same delivery the customer would be
  //    shopping for right now.
  const inventoryParams = usePairingInventoryParams();
  const { servingSize, isLoading: isServingSizeLoading } =
    useCustomerServingSize();

  // Dietary restrictions are NOT auto-applied by the customer-pairings
  // endpoint — we have to echo each active flag (`is_vegan`,
  // `is_gluten_free`, etc.) back to the server as a query param, same
  // pattern `useReadyToEatProducts` uses.
  const { data: dietaryRestrictions, isLoading: isDietaryRestrictionsLoading } =
    useCustomerDietaryRestrictions();

  const dietaryFlags = useMemo<
    Partial<Record<DietaryRestrictionFlag, true>>
  >(() => {
    const flags: Partial<Record<DietaryRestrictionFlag, true>> = {};
    // Note: we intentionally do NOT filter on `restriction.selected` here.
    // The v2 customer dietary-restrictions endpoint already narrows its
    // response to the customer's actives; EditDelivery uses the same
    // pattern (see getDietaryFilters in pages/customize/EditDelivery) and
    // filtering on `.selected` here was silently dropping diet flags like
    // `is_vegan` that come back without `selected: true` set.
    for (const restriction of dietaryRestrictions?.dietary_restrictions ?? []) {
      if (isDietaryRestrictionFlag(restriction.field_name)) {
        flags[restriction.field_name] = true;
      }
    }
    return flags;
  }, [dietaryRestrictions?.dietary_restrictions]);

  const {
    data: customerPairingsData,
    isLoading: isCustomerPairingsLoading,
    isError: isCustomerPairingsError,
  } = useCustomerPairingsV3({
    queryParams: {
      has_inventory: true,
      hide_dislike: true,
      // Ask the backend to drop pairings the customer has previously
      // marked as "never again." Complements the client-side filter that
      // excludes pairings they've actually ordered.
      exclude_nevers: true,
      limit: CANDIDATE_PAIRINGS_LIMIT,
      ...(servingSize !== undefined ? { servings: servingSize } : {}),
      ...dietaryFlags,
      ...(inventoryParams ?? {}),
    },
    options: {
      // Wait for plan serving size, dietary restrictions, and inventory
      // params (which themselves depend on the next editable order) so
      // we send a fully-scoped query the first time instead of firing
      // one that's missing filters and then a second once they resolve.
      enabled:
        !isServingSizeLoading &&
        !isDietaryRestrictionsLoading &&
        inventoryParams !== undefined,
    },
  });

  const eligibleCatalogCount = customerPairingsData?.count ?? 0;
  const novelCount = Math.max(0, eligibleCatalogCount - triedCount);

  // 3. Hydrate full pairing details for the full candidate pool returned
  //    by the customer-pairings endpoint (already prioritized server-side
  //    and filtered to the customer's plan). We pull details for the whole
  //    pool so we can filter by `recipe_score` below before picking the
  //    display set.
  //
  //    We also exclude pairings the customer has already ordered — the
  //    whole point of this deflection is surfacing recipes they haven't
  //    tried, so seeing last month's dinner in the grid defeats the
  //    reframe. Note this filter applies to the display list only; the
  //    headline `novelCount` stat already accounts for tried recipes via
  //    `eligibleCatalogCount − triedCount`.
  const candidateIds = useMemo(() => {
    const results = customerPairingsData?.results ?? [];
    return results
      .map((item) => item.id)
      .filter((id) => !triedPairingIds.has(id));
  }, [customerPairingsData?.results, triedPairingIds]);

  const {
    data: pairingsData,
    isLoading: isPairingsLoading,
    isError: isPairingsError,
  } = usePairings({
    ids: candidateIds,
    expand: [...PAIRING_EXPAND],
    limit: candidateIds.length,
    options: {
      enabled: candidateIds.length > 0,
    },
  });

  // 4. Resolve tag groups for every tag referenced by the candidate
  //    pairings. The v3 pairings endpoint returns tag IDs (not expanded
  //    Tag objects), so we can't tell which tags belong to the primary-
  //    protein group (tag_group_id 75) from the pairings response alone.
  //    Round-trip the unique tag IDs through `useTagsV3` to build a
  //    Map<tagId, tag_group_id>.
  const allCandidateTagIds = useMemo(() => {
    const ids = new Set<number>();
    for (const pairing of pairingsData?.results ?? []) {
      for (const tagId of pairing.tags ?? []) {
        ids.add(tagId);
      }
    }
    return Array.from(ids);
  }, [pairingsData?.results]);

  const { data: tagsData, isLoading: isTagsLoading } = useTagsV3(
    { ids: allCandidateTagIds, limit: allCandidateTagIds.length },
    { enabled: allCandidateTagIds.length > 0 }
  );

  const tagGroupByTagId = useMemo(() => {
    const map = new Map<number, number | null>();
    for (const tag of tagsData?.results ?? []) {
      map.set(tag.id, tag.tag_group_id);
    }
    return map;
  }, [tagsData?.results]);

  const recipes = useMemo(() => {
    const results = pairingsData?.results ?? [];
    // Preserve the backend ordering from useCustomerPairingsV3; usePairings
    // may return items in a different order on the wire.
    const byId = new Map<number, PairingListResultV3>();
    for (const pairing of results) {
      byId.set(pairing.id, pairing);
    }
    const orderedPool = candidateIds
      .map((id) => byId.get(id))
      .filter((p): p is PairingListResultV3 => p !== undefined);

    // `points` is a string on PairingListResultV3 ("4.00", "5.50", etc.).
    // Parse it once and treat unparseable / null values as 0 so those
    // recipes sort to the bottom without crashing the sort.
    const pointsOf = (p: PairingListResultV3): number => {
      if (p.points === null) return 0;
      const parsed = parseFloat(p.points);
      return Number.isFinite(parsed) ? parsed : 0;
    };

    // Prefer high-scored recipes, but fall back to lower-scored ones so
    // we always fill the grid when we have enough candidates.
    const highQuality = orderedPool.filter(
      (p) =>
        p.recipe_score !== null &&
        p.recipe_score >= HIGH_QUALITY_RECIPE_SCORE_MIN
    );

    if (highQuality.length >= NOVEL_RECIPES_DISPLAY_COUNT) {
      // When we have more candidates than we can show, lead with the
      // most mouth-watering (high-cost, high-scoring) recipes first.
      const sorted = [...highQuality].sort((a, b) => pointsOf(b) - pointsOf(a));

      // Then attempt to diversify by primary protein: round-robin through
      // protein tag groups (Chicken, Salmon, Tofu, etc.) picking the
      // highest-points recipe in each. For customers with enough variety
      // this yields "a bison dish, a salmon dish, a chicken dish…"
      // instead of twelve chicken dinners.
      //
      // When a customer's dietary restrictions narrow the pool to one
      // or two primary proteins (e.g. vegan → mostly tofu/beans), the
      // "uncategorized" bucket and falling back to remaining-by-points
      // fills the grid without leaving holes.
      return diversifyByPrimaryProtein(
        sorted,
        NOVEL_RECIPES_DISPLAY_COUNT,
        tagGroupByTagId
      );
    }

    // Below the display threshold — keep the server-side ordering for both
    // tiers so we don't amplify noise from scarce data.
    const highQualityIds = new Set(highQuality.map((p) => p.id));
    const fallback = orderedPool.filter((p) => !highQualityIds.has(p.id));

    return [...highQuality, ...fallback].slice(0, NOVEL_RECIPES_DISPLAY_COUNT);
  }, [pairingsData?.results, candidateIds, tagGroupByTagId]);

  const isLoading =
    isOrdersLoading ||
    isServingSizeLoading ||
    isDietaryRestrictionsLoading ||
    isCustomerPairingsLoading ||
    (candidateIds.length > 0 && isPairingsLoading) ||
    (allCandidateTagIds.length > 0 && isTagsLoading);

  const isError = isOrdersError || isCustomerPairingsError || isPairingsError;

  return {
    recipes,
    triedCount,
    eligibleCatalogCount,
    novelCount,
    isLoading,
    isError,
  };
}

/**
 * Returns the tag id of this pairing's primary-protein tag
 * (tag_group_id === PRIMARY_PROTEIN_TAG_GROUP_ID), or `null` if the
 * pairing has no primary-protein tag or its tag groups haven't been
 * resolved yet.
 *
 * A pairing could theoretically have multiple primary-protein tags
 * (e.g. a surf-and-turf dish) — in that case we pick the first one we
 * see. Tag group lookup happens via the Map passed in by the caller,
 * which is built from a separate `useTagsV3` request since the v3
 * pairings endpoint returns tag IDs rather than expanded Tag objects.
 */
function getPrimaryProteinTagId(
  pairing: PairingListResultV3,
  tagGroupByTagId: Map<number, number | null>
): number | null {
  for (const tagId of pairing.tags ?? []) {
    if (tagGroupByTagId.get(tagId) === PRIMARY_PROTEIN_TAG_GROUP_ID) {
      return tagId;
    }
  }
  return null;
}

/**
 * Pick up to `count` pairings from `sorted` (already sorted by whatever
 * ranking signal the caller cares about, e.g. points desc), biasing
 * toward diversity of primary protein.
 *
 * Algorithm:
 * 1. Bucket pairings by their primary-protein tag id. Pairings without
 *    a primary protein go into a single "uncategorized" bucket.
 * 2. Round-robin through the buckets, taking the next-best-ranked
 *    pairing from each, until we hit `count` or all buckets are empty.
 * 3. Preserve the input order within each bucket so our ranking signal
 *    still wins ties.
 *
 * If the customer only has one protein bucket available (e.g. a vegan
 * customer whose pool is all tofu/bean dishes), this degrades to just
 * taking the top `count` by rank — no holes, no special-casing.
 */
function diversifyByPrimaryProtein(
  sorted: PairingListResultV3[],
  count: number,
  tagGroupByTagId: Map<number, number | null>
): PairingListResultV3[] {
  if (sorted.length <= count) return sorted;

  // Stable bucketing by protein tag id. We use string keys so the
  // "uncategorized" bucket can live alongside numeric-id buckets in
  // a single map without ambiguity.
  const UNCATEGORIZED_KEY = 'uncategorized';
  const buckets = new Map<string, PairingListResultV3[]>();
  const bucketOrder: string[] = [];

  for (const pairing of sorted) {
    const proteinId = getPrimaryProteinTagId(pairing, tagGroupByTagId);
    const key = proteinId === null ? UNCATEGORIZED_KEY : String(proteinId);

    let bucket = buckets.get(key);
    if (!bucket) {
      bucket = [];
      buckets.set(key, bucket);
      bucketOrder.push(key);
    }
    bucket.push(pairing);
  }

  // Fast path: single bucket means no diversification to do.
  if (bucketOrder.length === 1) {
    return sorted.slice(0, count);
  }

  // Round-robin: cycle through bucketOrder, shift() the top of each
  // bucket until we hit `count` or every bucket is empty.
  const picked: PairingListResultV3[] = [];
  while (picked.length < count) {
    let tookAny = false;
    for (const key of bucketOrder) {
      if (picked.length >= count) break;
      const bucket = buckets.get(key);
      if (bucket && bucket.length > 0) {
        picked.push(bucket.shift()!);
        tookAny = true;
      }
    }
    if (!tookAny) break;
  }

  return picked;
}
